import html2canvas from 'html2canvas';
import {saveAs} from 'file-saver';
import moment from 'moment';

const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
const REPORT_WIDTH_PX = 794;
const REPORT_HEIGHT_PX = 1123;
const REPORT_PAGE_SAFE_BOTTOM_PX = 96;
const REPORT_SECTION_MIN_START_PX = 280;
const REPORT_PAGE_TOP_INSET_PX = 46;
const REPORT_FOOTER_MARGIN_RIGHT_PX = 56;
const REPORT_FOOTER_MARGIN_BOTTOM_PX = 24;
const REPORT_FOOTER_LOGO_SIZE_PX = 34;
const REPORT_FOOTER_GAP_PX = 8;
const PDF_TEXT_ENCODER = new TextEncoder();

type PdfPart = string | Uint8Array;

type PdfPageImage = {
  bytes: Uint8Array;
  width: number;
  height: number;
};

function lshPartByteLength(part: PdfPart) {
  return typeof part === 'string'
    ? PDF_TEXT_ENCODER.encode(part).length
    : part.length;
}

function lshPartsByteLength(parts: PdfPart[]) {
  return parts.reduce((total, part) => total + lshPartByteLength(part), 0);
}

function lshBytesFromBase64(base64: string) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function lshJpegBytesFromDataUrl(dataUrl: string) {
  return lshBytesFromBase64(dataUrl.split(',')[1] || '');
}

function lshAppendPdfObject(
  parts: PdfPart[],
  offsets: number[],
  objectId: number,
  body: PdfPart[],
) {
  offsets[objectId] = lshPartsByteLength(parts);
  parts.push(`${objectId} 0 obj\n`, ...body, '\nendobj\n');
}

function lshBuildPdfBlob(images: PdfPageImage[]) {
  const parts: PdfPart[] = ['%PDF-1.4\n'];
  const offsets: number[] = [];
  const kids = images.map((_, index) => `${3 + index * 3} 0 R`).join(' ');
  const maxObjectId = 2 + images.length * 3;

  lshAppendPdfObject(parts, offsets, 1, ['<< /Type /Catalog /Pages 2 0 R >>']);
  lshAppendPdfObject(parts, offsets, 2, [
    `<< /Type /Pages /Kids [${kids}] /Count ${images.length} >>`,
  ]);

  images.forEach((image, index) => {
    const pageObjectId = 3 + index * 3;
    const contentObjectId = pageObjectId + 1;
    const imageObjectId = pageObjectId + 2;
    const imageName = `Im${index + 1}`;
    const content = [
      'q',
      `${A4_WIDTH_PT} 0 0 ${A4_HEIGHT_PT} 0 0 cm`,
      `/${imageName} Do`,
      'Q',
    ].join('\n');

    lshAppendPdfObject(parts, offsets, pageObjectId, [
      [
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4_WIDTH_PT}`,
        `${A4_HEIGHT_PT}] /Resources << /XObject << /${imageName}`,
        `${imageObjectId} 0 R >> >> /Contents ${contentObjectId} 0 R >>`,
      ].join(' '),
    ]);
    lshAppendPdfObject(parts, offsets, contentObjectId, [
      `<< /Length ${
        PDF_TEXT_ENCODER.encode(content).length
      } >>\nstream\n${content}\nendstream`,
    ]);
    lshAppendPdfObject(parts, offsets, imageObjectId, [
      [
        `<< /Type /XObject /Subtype /Image /Width ${image.width}`,
        `/Height ${image.height} /ColorSpace /DeviceRGB`,
        `/BitsPerComponent 8 /Filter /DCTDecode /Length`,
        `${image.bytes.length} >>\nstream\n`,
      ].join(' '),
      image.bytes,
      '\nendstream',
    ]);
  });

  const xrefOffset = lshPartsByteLength(parts);
  parts.push(`xref\n0 ${maxObjectId + 1}\n0000000000 65535 f \n`);

  for (let objectId = 1; objectId <= maxObjectId; objectId += 1) {
    parts.push(
      `${String(offsets[objectId] || 0).padStart(10, '0')} 00000 n \n`,
    );
  }

  parts.push(
    `trailer\n<< /Size ${
      maxObjectId + 1
    } /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`,
  );

  return new Blob(parts, {type: 'application/pdf'});
}

function lshWaitForFrameLoad(frame: HTMLIFrameElement) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, 300);
    frame.onload = () => resolve();
  });
}

async function lshWaitForReportAssets(documentRef: Document) {
  if (documentRef.fonts?.ready) {
    await documentRef.fonts.ready;
  }

  const images = Array.from(documentRef.images);

  await Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.onload = () => resolve();
          image.onerror = () => resolve();
        }),
    ),
  );
}

function lshCreateReportFrame(reportHtml: string) {
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.left = '-10000px';
  frame.style.top = '0';
  frame.style.width = `${REPORT_WIDTH_PX}px`;
  frame.style.height = `${REPORT_HEIGHT_PX}px`;
  frame.style.opacity = '0';
  frame.style.pointerEvents = 'none';
  document.body.appendChild(frame);

  const documentRef = frame.contentDocument;

  if (!documentRef) {
    document.body.removeChild(frame);
    throw new Error('Report frame could not be created.');
  }

  documentRef.open();
  documentRef.write(reportHtml);
  documentRef.close();

  return {frame, documentRef};
}

function lshPrepareReportDocument(documentRef: Document) {
  const style = documentRef.createElement('style');
  style.textContent = `
    html,
    body {
      width: ${REPORT_WIDTH_PX}px;
      min-height: ${REPORT_HEIGHT_PX}px;
      overflow: visible;
    }

    .report {
      min-height: ${REPORT_HEIGHT_PX}px;
      padding: 46px 56px 72px;
    }

    .report-footer {
      display: none;
    }
  `;
  documentRef.head.appendChild(style);
}

function lshGetElementTop(element: HTMLElement, body: HTMLElement) {
  const elementRect = element.getBoundingClientRect();
  const bodyRect = body.getBoundingClientRect();
  return elementRect.top - bodyRect.top + body.scrollTop;
}

function lshPushElementToNextPage(element: HTMLElement, body: HTMLElement) {
  const top = lshGetElementTop(element, body);
  const height = element.getBoundingClientRect().height;
  const safePageHeight = REPORT_HEIGHT_PX - REPORT_PAGE_SAFE_BOTTOM_PX;
  const pageTop = Math.floor(top / REPORT_HEIGHT_PX) * REPORT_HEIGHT_PX;
  const pageBottom = pageTop + safePageHeight;
  const bottom = top + height;
  const isSection = element.classList.contains('report-section');

  if (isSection && pageBottom - top < REPORT_SECTION_MIN_START_PX) {
    const currentMargin = parseFloat(element.style.marginTop || '0') || 0;
    const nextPageTop = pageTop + REPORT_HEIGHT_PX + REPORT_PAGE_TOP_INSET_PX;
    element.style.marginTop = `${currentMargin + nextPageTop - top}px`;
    return;
  }

  if (height >= safePageHeight) return;

  if (bottom <= pageBottom) return;

  const currentMargin = parseFloat(element.style.marginTop || '0') || 0;
  const nextPageTop = pageTop + REPORT_HEIGHT_PX;
  element.style.marginTop = `${currentMargin + nextPageTop - top}px`;
}

function lshPaginateReportDocument(documentRef: Document) {
  const body = documentRef.body;

  for (let pass = 0; pass < 4; pass += 1) {
    Array.from(
      documentRef.querySelectorAll<HTMLElement>('.report-section'),
    ).forEach((element) => lshPushElementToNextPage(element, body));

    Array.from(
      documentRef.querySelectorAll<HTMLElement>('.report-card'),
    ).forEach((element) => lshPushElementToNextPage(element, body));
  }
}

function lshDrawReportFooter(
  context: CanvasRenderingContext2D,
  pageCanvas: HTMLCanvasElement,
  footerLogo?: HTMLImageElement | null,
) {
  const scale = pageCanvas.width / REPORT_WIDTH_PX;
  const logoSize = REPORT_FOOTER_LOGO_SIZE_PX * scale;
  const marginRight = REPORT_FOOTER_MARGIN_RIGHT_PX * scale;
  const marginBottom = REPORT_FOOTER_MARGIN_BOTTOM_PX * scale;
  const gap = REPORT_FOOTER_GAP_PX * scale;
  const logoX = pageCanvas.width - marginRight - logoSize;
  const logoY = pageCanvas.height - marginBottom - logoSize;
  const text = 'Generated with Khula app';

  context.save();
  context.fillStyle = '#687672';
  context.font = `700 italic ${10 * scale}px Arial, sans-serif`;
  context.textBaseline = 'middle';

  const textWidth = context.measureText(text).width;
  context.fillText(text, logoX - gap - textWidth, logoY + logoSize / 2);

  if (footerLogo?.complete && footerLogo.naturalWidth) {
    context.drawImage(footerLogo, logoX, logoY, logoSize, logoSize);
  }

  context.restore();
}

async function lshRenderReportImages(reportHtml: string) {
  const {frame, documentRef} = lshCreateReportFrame(reportHtml);

  try {
    await lshWaitForFrameLoad(frame);
    lshPrepareReportDocument(documentRef);
    await lshWaitForReportAssets(documentRef);
    lshPaginateReportDocument(documentRef);

    const body = documentRef.body;
    const captureHeight = Math.max(body.scrollHeight, REPORT_HEIGHT_PX);
    const canvasOptions: NonNullable<Parameters<typeof html2canvas>[1]> = {
      backgroundColor: '#ffffff',
      scale: 2,
      width: REPORT_WIDTH_PX,
      windowWidth: REPORT_WIDTH_PX,
      windowHeight: captureHeight,
    };
    canvasOptions.useCORS = true;

    const canvas = await html2canvas(body, canvasOptions);
    const pageCanvasHeight = Math.floor(
      (REPORT_HEIGHT_PX / REPORT_WIDTH_PX) * canvas.width,
    );
    const images: PdfPageImage[] = [];
    const footerLogo =
      documentRef.querySelector<HTMLImageElement>('.report-footer img');

    for (let top = 0; top < canvas.height; top += pageCanvasHeight) {
      const pageCanvas = document.createElement('canvas');
      const context = pageCanvas.getContext('2d');

      pageCanvas.width = canvas.width;
      pageCanvas.height = pageCanvasHeight;

      if (context) {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        context.drawImage(
          canvas,
          0,
          top,
          canvas.width,
          Math.min(pageCanvasHeight, canvas.height - top),
          0,
          0,
          canvas.width,
          Math.min(pageCanvasHeight, canvas.height - top),
        );
        lshDrawReportFooter(context, pageCanvas, footerLogo);
      }

      images.push({
        bytes: lshJpegBytesFromDataUrl(
          pageCanvas.toDataURL('image/jpeg', 0.92),
        ),
        width: pageCanvas.width,
        height: pageCanvas.height,
      });
    }

    return images;
  } finally {
    document.body.removeChild(frame);
  }
}

export function lshBuildAnalyticsReportFileName(companyName: string) {
  const cleanCompanyName =
    companyName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'analytics';

  return `${cleanCompanyName}-analytics-report-${moment().format(
    'YYYY-MM-DD-HHmm',
  )}.pdf`;
}

export async function lshSaveAnalyticsReportPdf(
  reportHtml: string,
  fileName: string,
) {
  const images = await lshRenderReportImages(reportHtml);
  const pdfBlob = lshBuildPdfBlob(images);
  saveAs(pdfBlob, fileName);
}
